import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  Calendar,
  CheckCircle2,
  Clock,
  ExternalLink,
  Loader2,
  MapPin,
  Search,
  User,
} from 'lucide-react';

import { Badge } from '@/app/components/ui/badge';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Skeleton } from '@/app/components/ui/skeleton';
import {
  useComplianceIncident,
  useCompleteInvestigation,
  useMarkNotified,
} from '@/hooks/use-compliance-incidents';
import { useOrgTimeZone, useOrgToday } from '@/hooks/use-settings';
import { formatDateOnly, formatDateTimeInTimeZone } from '@/lib/date-utils';
import { differenceInCalendarDays, parseISO } from 'date-fns';
import type {
  ComplianceIncidentSeverity,
  ComplianceIncidentStatus,
  IncidentNotification,
} from '@/lib/types';

const STATUS_LABELS: Record<ComplianceIncidentStatus, string> = {
  reported: 'Reported',
  under_investigation: 'Under Investigation',
  corrective_action: 'Corrective Action',
  closed: 'Closed',
};

const STATUS_COLORS: Record<ComplianceIncidentStatus, string> = {
  reported: 'bg-blue-100 text-blue-800',
  under_investigation: 'bg-yellow-100 text-yellow-800',
  corrective_action: 'bg-orange-100 text-orange-800',
  closed: 'bg-green-100 text-green-800',
};

const SEVERITY_LABELS: Record<ComplianceIncidentSeverity, string> = {
  minor: 'Minor',
  moderate: 'Moderate',
  major: 'Major',
  critical: 'Critical',
  fatality: 'Fatality',
};

const SEVERITY_COLORS: Record<ComplianceIncidentSeverity, string> = {
  minor: 'bg-gray-100 text-gray-800',
  moderate: 'bg-yellow-100 text-yellow-800',
  major: 'bg-orange-100 text-orange-800',
  critical: 'bg-red-100 text-red-800',
  fatality: 'bg-red-200 text-red-900',
};

export function NotificationCard({ notification }: { notification: IncidentNotification }) {
  const timeZone = useOrgTimeZone();
  const orgToday = useOrgToday();
  const days = differenceInCalendarDays(
    parseISO(notification.due_date),
    parseISO(orgToday)
  );
  const isSubmitted = !!notification.submitted_at;
  const isInternal = notification.notification_type === 'internal';
  const isOverdue = !isSubmitted && !isInternal && days < 0;

  return (
    <div className="flex items-center justify-between rounded-md border p-3">
      <div>
        <div className="flex items-center gap-2">
          <span className="font-medium">
            {notification.notification_type === 'irata_7day'
              ? 'IRATA 7-Day Notification'
              : notification.notification_type.replace('_', ' ').toUpperCase()}
          </span>
          {isSubmitted ? (
            <Badge className="bg-green-100 text-green-800">
              <CheckCircle2 className="mr-1 h-3 w-3" />
              Submitted
            </Badge>
          ) : isInternal ? (
            <Badge variant="secondary">Internal tracking</Badge>
          ) : isOverdue ? (
            <Badge variant="destructive">
              <AlertTriangle className="mr-1 h-3 w-3" />
              Overdue
            </Badge>
          ) : (
            <Badge className="bg-yellow-100 text-yellow-800">
              <Clock className="mr-1 h-3 w-3" />
              {days} day{days !== 1 ? 's' : ''} remaining
            </Badge>
          )}
        </div>
        {(!isInternal || notification.submitted_at || notification.reference_number) && (
          <p className="mt-1 text-sm text-muted-foreground">
            {!isInternal && <>Due: {formatDateOnly(notification.due_date)}</>}
            {notification.submitted_at && (
              <>
                {!isInternal && <> &middot; </>}
                Submitted: {formatDateTimeInTimeZone(notification.submitted_at, timeZone, { dateStyle: 'medium' })}
              </>
            )}
            {notification.reference_number && (
              <> &middot; Ref: {notification.reference_number}</>
            )}
          </p>
        )}
      </div>
    </div>
  );
}

export function ComplianceIncidentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const incidentId = Number(id);
  const { data: incident, isLoading } = useComplianceIncident(incidentId);
  const completeInvestigation = useCompleteInvestigation();
  const markNotified = useMarkNotified();

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  if (!incident) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertTriangle className="mb-4 h-12 w-12 text-muted-foreground" />
        <h3 className="text-lg font-semibold">Incident not found</h3>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => navigate('/compliance/incidents')}
        >
          Back to Register
        </Button>
      </div>
    );
  }

  const pendingNotification = incident.notifications.find((n) => !n.submitted_at);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/compliance/incidents')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">{incident.incident_number}</h1>
              <Badge className={STATUS_COLORS[incident.status]}>
                {STATUS_LABELS[incident.status]}
              </Badge>
              <Badge className={SEVERITY_COLORS[incident.severity]}>
                {SEVERITY_LABELS[incident.severity]}
              </Badge>
            </div>
            <p className="text-lg text-muted-foreground">{incident.title}</p>
          </div>
        </div>

        <div className="flex gap-2">
          {incident.status === 'reported' && !incident.investigation && (
            <Button onClick={() => navigate(`/compliance/incidents/${incident.id}/investigate`)}>
              <Search className="mr-2 h-4 w-4" />
              Start Investigation
            </Button>
          )}
          {incident.investigation && !incident.investigation.completed_at && (
            <Button
              onClick={() => completeInvestigation.mutate(incident.id)}
              disabled={completeInvestigation.isPending}
            >
              {completeInvestigation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              <CheckCircle2 className="mr-2 h-4 w-4" />
              Complete Investigation
            </Button>
          )}
          {pendingNotification && (
            <Button
              variant="outline"
              onClick={() => markNotified.mutate({ incidentId: incident.id })}
              disabled={markNotified.isPending}
            >
              {markNotified.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Mark IRATA Notified
            </Button>
          )}
        </div>
      </div>

      {/* Incident Details */}
      <Card>
        <CardHeader>
          <CardTitle>Incident Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Date</p>
                <p className="font-medium">
                  {formatDateOnly(incident.date)}
                  {incident.time && ` at ${incident.time}`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Location</p>
                <p className="font-medium">{incident.location}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Reported By</p>
                <p className="font-medium">{incident.reported_by_username || 'Unknown'}</p>
              </div>
            </div>
          </div>

          {(incident.project_name || incident.project_number) && (
            <div className="grid gap-4 sm:grid-cols-2">
              {incident.project_name && (
                <div>
                  <p className="text-sm text-muted-foreground">Project</p>
                  <p className="font-medium">{incident.project_name}</p>
                </div>
              )}
              {incident.project_number && (
                <div>
                  <p className="text-sm text-muted-foreground">Project Number</p>
                  <p className="font-medium">{incident.project_number}</p>
                </div>
              )}
            </div>
          )}

          <div>
            <p className="text-sm text-muted-foreground">Description</p>
            <p className="mt-1 whitespace-pre-wrap">{incident.description}</p>
          </div>

          {incident.injury_details && (
            <div>
              <p className="text-sm text-muted-foreground">Injury Details</p>
              <p className="mt-1 whitespace-pre-wrap">{incident.injury_details}</p>
            </div>
          )}

          <div>
            <p className="text-sm text-muted-foreground">Immediate Actions Taken</p>
            <p className="mt-1 whitespace-pre-wrap">{incident.immediate_actions_taken}</p>
          </div>

          {incident.near_miss && (
            <div className="rounded-md border border-blue-200 bg-blue-50 p-3">
              <div className="flex items-center gap-2">
                <ExternalLink className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-800">
                  Escalated from Near-Miss:{' '}
                  <Button
                    variant="link"
                    className="h-auto p-0 text-blue-700"
                    onClick={() => navigate(`/safety/near-misses/${incident.near_miss}`)}
                  >
                    {incident.near_miss_title || `Near-Miss #${incident.near_miss}`}
                  </Button>
                </span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Persons Involved */}
      {incident.persons_involved && incident.persons_involved.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Persons Involved</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {incident.persons_involved.map((person, i) => (
                <div key={i} className="flex items-center gap-4 rounded-md border p-3">
                  <User className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">{person.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {person.role}
                      {person.injury_type && ` — ${person.injury_type}`}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Notification Tracker */}
      {incident.notifications.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Notification Tracker</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {incident.notifications.map((notification) => (
              <NotificationCard key={notification.id} notification={notification} />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Investigation Section */}
      {incident.investigation && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Investigation
              {incident.investigation.completed_at ? (
                <Badge className="bg-green-100 text-green-800">Completed</Badge>
              ) : (
                <Badge className="bg-yellow-100 text-yellow-800">In Progress</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <p className="text-sm text-muted-foreground">Investigator</p>
                <p className="font-medium">
                  {incident.investigation.investigator_username || 'Unknown'}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Investigation Date</p>
                <p className="font-medium">{formatDateOnly(incident.investigation.investigation_date)}</p>
              </div>
            </div>

            <div>
              <p className="text-sm text-muted-foreground">Root Cause</p>
              <p className="mt-1 whitespace-pre-wrap">{incident.investigation.root_cause}</p>
            </div>

            {incident.investigation.contributing_factors && (
              <div>
                <p className="text-sm text-muted-foreground">Contributing Factors</p>
                <p className="mt-1 whitespace-pre-wrap">
                  {incident.investigation.contributing_factors}
                </p>
              </div>
            )}

            <div>
              <p className="text-sm text-muted-foreground">Findings</p>
              <p className="mt-1 whitespace-pre-wrap">{incident.investigation.findings}</p>
            </div>

            <div>
              <p className="text-sm text-muted-foreground">Recommendations</p>
              <p className="mt-1 whitespace-pre-wrap">{incident.investigation.recommendations}</p>
            </div>

            {/* Corrective Actions */}
            {incident.investigation.corrective_actions &&
              incident.investigation.corrective_actions.length > 0 && (
                <div>
                  <p className="mb-2 text-sm font-medium text-muted-foreground">
                    Corrective Actions
                  </p>
                  <div className="space-y-2">
                    {incident.investigation.corrective_actions.map((action, i) => (
                      <div key={i} className="flex items-center justify-between rounded-md border p-3">
                        <div>
                          <p className="font-medium">{action.action}</p>
                          <p className="text-sm text-muted-foreground">
                            Assigned to: {action.assignee} &middot; Due: {formatDateOnly(action.due_date)}
                          </p>
                        </div>
                        <Badge
                          className={
                            action.status === 'completed'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }
                        >
                          {action.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
